/**
 * Safe wrapper around the experimental `navigator.keyboard.getLayoutMap()`
 * API (issue #87): resolves the user's actual physical-key layout (e.g.
 * AZERTY) so the controls overlay's keycaps show their real letters instead
 * of always assuming QWERTY. Not yet in TypeScript's DOM lib, so the shape
 * used here is declared locally and accessed via a narrow duck-typed cast —
 * the same pattern main.ts already uses for other browser-only globals.
 *
 * The caller (`nav`) is passed in rather than read from the global
 * `navigator` directly so this stays plain-object-testable in Node, mirroring
 * the injected-storage pattern in hudPrefs.ts. Every failure mode (API
 * absent on this browser — Firefox/Safari at time of writing — the promise
 * rejecting, or a malformed return value) resolves to `null` rather than
 * throwing; `controlsBindings.ts`'s `resolveKeyLabel` falls back to QWERTY
 * whenever it receives null, so callers must never block rendering on this
 * promise (parent spec: "never block render on the promise").
 */

interface KeyboardLayoutMapLike {
  entries(): Iterable<[string, string]>;
}

interface NavigatorKeyboardLike {
  keyboard?: {
    getLayoutMap?: () => Promise<KeyboardLayoutMapLike>;
  };
}

/**
 * Resolves the user's physical-key layout as a plain `Map` (code -> the
 * character that key types), or `null` if the API is unavailable, throws
 * synchronously, or its promise rejects. `nav` is typically `window.navigator`
 * at the real call site, injected here for testability.
 */
export async function loadKeyboardLayoutMap(nav: unknown): Promise<Map<string, string> | null> {
  try {
    const keyboard = (nav as NavigatorKeyboardLike | undefined)?.keyboard;
    const getLayoutMap = keyboard?.getLayoutMap;
    if (typeof getLayoutMap !== 'function') return null;
    const layoutMap = await getLayoutMap.call(keyboard);
    return new Map(layoutMap.entries());
  } catch {
    return null;
  }
}
